import { Button, Stack, Tab, Tabs, Typography, Link as B2BLink } from '@evaneos/b2b-ui';

import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';

import { Query } from '../../../design-system/types';
import s from '../../../shared/components/layout/ContextualNavbar.css';
import { displayFilters } from '../../../resources/filters';
import { useGetInboxCount } from '../../../resources/inbox/hooks';
import { useTrackEventAmplitude } from '../../../shared/analytics';
import { AllTranslationKeys } from '../../../shared/intl/types';
import { useAppDispatch } from '../../../shared/redux';
import { parseQueryString } from '../../../shared/utilities/queryString';
import { NavigationToggle } from '../../../shared/components/NavigationToggle';
import { useHasDossiers } from '../../../resources/dossier-overview/hooks';

const periods = ['today', 'tomorrow', 'this_week'] as const;

export const TodoNavbar = (): JSX.Element => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const query = parseQueryString(location.search) as Query;
    const inboxCount = useGetInboxCount(query);
    const hasDossiers = useHasDossiers();

    const handleFilterButtonClicked = () => {
        dispatch(displayFilters());
        trackEvent((ampli, defaultProperties) => {
            ampli.rmFilterClicked({ ...defaultProperties, category: 'todo' });
        });
    };

    const handlePeriodClick = (period: (typeof periods)[number]) => {
        trackEvent((ampli, defaultProperties) => {
            ampli.toDoSubTabClicked({
                ...defaultProperties,
                to_do_sub_tab_name: period,
            });
        });
    };

    const periodFromLocation = location.pathname.match(/\/todo\/([^/]+)/)?.[1];
    return (
        <nav className={s.navbarContainer}>
            <div className={s.pageSwitch}>
                <NavigationToggle selected="todo" inboxCount={inboxCount} />
            </div>
            <div className={s.navbarContent}>
                <Tabs value={periodFromLocation}>
                    {periods.map((period) => (
                        <Tab
                            disableRipple
                            sx={{
                                textTransform: 'capitalize',
                                color: 'text.primary',
                            }}
                            component={NavLink}
                            key={`todo.${period}.name`}
                            to={{
                                pathname: `/todo/${period}`,
                                search: location.search,
                            }}
                            label={formatMessage({
                                id: `todo.${period}` as AllTranslationKeys,
                            })}
                            value={period}
                            onClick={() => handlePeriodClick(period)}
                        />
                    ))}
                </Tabs>
            </div>
            <Stack direction="row" gap={2} alignItems="center">
                <Typography variant="body1">
                    <B2BLink data-testid={'cta-agency-capacity'} href="/agency-manager/capacity">
                        Agency capacity
                    </B2BLink>
                </Typography>

                <Button
                    data-testid="process-navbar-filter-button"
                    disabled={!hasDossiers}
                    variant="outlined"
                    onClick={handleFilterButtonClicked}
                >
                    <FormattedMessage id="processes.filters.filters" />
                </Button>
            </Stack>
        </nav>
    );
};
